import { ChiroUpAPI } from '@chiroup/client-core/functions/ChiroUpAPI';
import { getId } from '@chiroup/core/functions/getId';
import { ReferenceCodeDisplay } from '@chiroup/core/types/ReferenceCode.type';

const itemService = () => {
  const list = async (type: string, params: any, clinicId: number, userId: string) => {
    const res = await ChiroUpAPI.get('api', `/${clinicId}/${type}`, {
      queryParams: params,
    });

    const returnValue =
      type !== 'evaluation'
        ? type === 'exercise'
          ? {
              data: res?.data?.map((item: any) => ({
                ...item,
                $id: getId(item),
              })),
              skip: params.skip + params.limit,
            }
          : {
              data: res.map((item: any) => ({
                ...item,
                $id: getId(item),
              })),
              skip: params.skip + params.limit,
            }
        : res;

    return returnValue;
  };

  const findOne = async <ItemType>(
    type: string,
    clinicId = -1,
    userId = '',
    id: string
  ): Promise<ItemType> => {
    const res = await ChiroUpAPI.get('api', `/${clinicId}/${type}/${id}`, {});
    const item = res?.[0];

    return item;
  };

  const findOneByPath = async <ItemType>(
    type: string,
    clinicId = -1,
    path: string
  ): Promise<ItemType> => {
    const res = await ChiroUpAPI.get('api', `/${clinicId}/clinical-item/${type}/path/${path}`, {});
    const item = res?.[0];

    return item;
  };

  const save = async <ItemType>(
    type: string,
    clinicId = -1,
    userId: string,
    id: string,
    body: Partial<ItemType>
  ): Promise<ItemType> => {
    const res = await ChiroUpAPI.patch('api', `/${clinicId}/${type}/${id}`, {
      body,
    });
    const item = res;

    if (item.tags?.length) {
      item.tags = item.tags.map((tag: any) => (typeof tag === 'string' ? tag : tag.tag));
    }

    return item;
  };

  const add = async <ItemType>(
    type: string,
    clinicId = -1,
    userId: string,
    body: Partial<ItemType>
  ): Promise<ItemType> => {
    const res = await ChiroUpAPI.post('api', `/${clinicId}/${type}`, {
      body,
    });

    return res;
  };

  const del = async (
    type: string,
    clinicId = -1,
    userId: string,
    id: number,
    clinic: boolean
  ): Promise<void> => {
    let endpoint = `/${clinicId}/${type}/${id}`;

    if (clinic) {
      endpoint = `/${clinicId}/${type}/${id}/clinic`;
    }

    return ChiroUpAPI.del('api', endpoint, {});
  };

  const favorite = async (clinicId = -1, type: string, ids: number[], favorite?: boolean) => {
    return ChiroUpAPI.patch('api', `/${clinicId}/fav/${type}`, {
      body: { ids, favorite },
    });
  };

  const favoriteCodesToItems = async (
    type: string,
    favorite: number,
    conditionId: number,
    code: string,
    codeSet?: string,
    clinicId?: number
  ) => {
    return ChiroUpAPI.put('api', `/settings/${clinicId}/favorite/codes-to-items`, {
      body: { type, conditionId, favorite, code, codeSet },
    });
  };

  // Save edited reference codes to the user's My Version (backend creates it if none).
  const saveConditionReferenceCodes = async (
    clinicId: number,
    conditionId: number,
    referenceCodes: ReferenceCodeDisplay[]
  ): Promise<{ userVersionId: number }> => {
    return ChiroUpAPI.put('api', `/${clinicId}/condition/${conditionId}/reference-codes`, {
      body: { referenceCodes },
    });
  };

  const languageCreate = async (params: any): Promise<any> => {
    const res = await ChiroUpAPI.post('api', '/language', { body: params });

    return res;
  };

  return {
    list,
    findOne,
    findOneByPath,
    save,
    del,
    add,
    favorite,
    languageCreate,
    favoriteCodesToItems,
    saveConditionReferenceCodes,
  };
};

export default itemService();
